import type { AxiosRequestConfig } from 'axios'
import type { AuthRefreshHandlers } from './classes/login'
import type { FutManagerApiConfigOptions } from './types/FutManagerApiConfigOptions'
import { FutManagerAxiosClass } from './FutManagerAxios'
import { installAuthRefreshInterceptor } from './interceptors/AuthRefreshInterceptor'
import authTokenInterceptor from './interceptors/AuthTokenInterceptor'

export class FutManagerApiClass {
  authToken: string | null | undefined = undefined

  authTokenInterceptorId: number | undefined

  adminAuthTokenInterceptorId: number | undefined

  authRefreshInterceptorId: number | undefined

  adminAuthRefreshInterceptorId: number | undefined

  authRefreshHandlers: AuthRefreshHandlers | undefined

  axios = new FutManagerAxiosClass()

  configure(options: FutManagerApiConfigOptions) {
    const timeout = options.timeout ?? 60_000
    const config: AxiosRequestConfig = {
      baseURL: options.url,
      timeout,
    }

    this.axios.installAxios(config, options.accept)

    if (options.adminUrl) {
      this.axios.installAdminAxios(
        { baseURL: options.adminUrl, timeout },
        options.accept,
      )
    }

    this.authRefreshHandlers = options.authRefresh
    this.installAuthRefresh()

    if (this.authToken) {
      this.setAuthToken(this.authToken)
    }
  }

  setAuthToken(token: string) {
    this.removeAuthToken()
    this.authToken = token
    if (!token) {
      return
    }
    if (this.axios.axiosInstance) {
      this.authTokenInterceptorId = this.axios.axiosInstance.interceptors.request.use(
        authTokenInterceptor(token),
      )
    }
    if (this.axios.adminAxiosInstance) {
      this.adminAuthTokenInterceptorId = this.axios.adminAxiosInstance.interceptors.request.use(
        authTokenInterceptor(token),
      )
    }
  }

  removeAuthToken() {
    this.authToken = null
    if (this.axios.axiosInstance && this.authTokenInterceptorId !== undefined) {
      this.axios.axiosInstance.interceptors.request.eject(this.authTokenInterceptorId)
    }
    if (this.axios.adminAxiosInstance && this.adminAuthTokenInterceptorId !== undefined) {
      this.axios.adminAxiosInstance.interceptors.request.eject(this.adminAuthTokenInterceptorId)
    }
    this.authTokenInterceptorId = undefined
    this.adminAuthTokenInterceptorId = undefined
  }

  private ejectAuthRefresh() {
    if (this.axios.axiosInstance && this.authRefreshInterceptorId !== undefined) {
      this.axios.axiosInstance.interceptors.response.eject(this.authRefreshInterceptorId)
    }
    if (this.axios.adminAxiosInstance && this.adminAuthRefreshInterceptorId !== undefined) {
      this.axios.adminAxiosInstance.interceptors.response.eject(this.adminAuthRefreshInterceptorId)
    }
    this.authRefreshInterceptorId = undefined
    this.adminAuthRefreshInterceptorId = undefined
  }

  private installAuthRefresh() {
    this.ejectAuthRefresh()

    const handlers = this.authRefreshHandlers
    const dashClient = this.axios.axiosInstance
    if (!handlers || !dashClient) {
      return
    }

    const clients = [dashClient]
    const adminClient = this.axios.adminAxiosInstance
    if (adminClient) {
      clients.push(adminClient)
    }

    const ids = installAuthRefreshInterceptor(
      clients,
      dashClient,
      handlers,
      token => this.setAuthToken(token),
    )

    this.authRefreshInterceptorId = ids[0]
    if (adminClient) {
      this.adminAuthRefreshInterceptorId = ids[1]
    }
  }
}

export const FutManagerApi = new FutManagerApiClass()
